import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface DateTimePickerProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  inputClassName?: string;
  label?: string;
  errorText?: string;
}

export const DateTimePicker = ({
  name,
  value,
  onChange,
  label,
  required,
  errorText,
}: DateTimePickerProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [hours, setHours] = useState("09");
  const [minutes, setMinutes] = useState("00");
  const [amPm, setAmPm] = useState("AM");
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

  const closePicker = () => setShowPicker(false);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const pickerElement = document.querySelector(".absolute.datepicker");
    if (pickerElement && !pickerElement.contains(event.target as Node)) {
      closePicker();
    }
  }, []);

  useEffect(() => {
    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPicker, handleClickOutside]);

  const generateCalendar = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const days = [];
    const blanks = (start.getDay() + 6) % 7;
    for (let i = 0; i < blanks; i++) days.push(null);
    for (let d = 1; d <= end.getDate(); d++)
      days.push(new Date(date.getFullYear(), date.getMonth(), d));
    return days;
  };

  const calendarDays = generateCalendar(currentDate);

  const changeMonth = (direction: number) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const onSelectDate = (date: Date) => setSelectedDate(date);

  const getDisplayValue = () => {
    if (!selectedDate) return "";
    let hour = parseInt(hours, 10);
    if (amPm === "PM" && hour < 12) hour += 12;
    if (amPm === "AM" && hour === 12) hour = 0;
    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(hour);
    selectedDateTime.setMinutes(parseInt(minutes, 10));
    return format(selectedDateTime, "yyyy-MM-dd HH:mm");
  };

  const hoursOptions = Array.from(Array(12).keys()).map((h) =>
    String(h).padStart(2, "0"),
  );
  const minutesOptions = Array.from(Array(60).keys()).map((m) =>
    String(m).padStart(2, "0"),
  );

  const togglePicker = (e: React.MouseEvent<HTMLInputElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const spaceRight = viewportWidth - rect.left;

    const top =
      spaceBelow > 416 || spaceBelow > spaceAbove
        ? rect.bottom + window.scrollY
        : rect.top - 416 + window.scrollY; // Adjust height as needed

    const left =
      spaceRight > 416
        ? rect.left + window.scrollX
        : viewportWidth - 416 + window.scrollX; // Adjust width as needed

    setPickerPosition({ top, left });
    setShowPicker(!showPicker);
  };

  return (
    <div className="relative mb-4">
      <label
        htmlFor={name}
        className="block mb-2 text-sm font-semibold text-gray-900 dark:text-white"
      >
        {label} {required ? <span className="text-red-500">*</span> : ""}
      </label>
      <input
        type="text"
        value={value}
        onClick={togglePicker}
        readOnly
        className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-sm text-left cursor-pointer outline-none focus:ring-0 focus:ring-offset-0 focus:ring-border focus:border-gray-300"
        placeholder="Select date and time"
      />
      {errorText && <p className="text-xs text-red-500 mt-1">{errorText}</p>}

      {showPicker &&
        createPortal(
          <div
            className="datepicker absolute z-50 bg-white border border-gray-200 rounded-md shadow-lg p-4"
            style={{
              top: pickerPosition.top,
              left: pickerPosition.left,
              position: "absolute",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => changeMonth(-1)}>&lt;</button>
              <div className="text-lg font-medium">
                {format(currentDate, "MMMM yyyy")}
              </div>
              <button onClick={() => changeMonth(1)}>&gt;</button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-sm text-gray-500">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 mt-1">
              {calendarDays.map((day, idx) => (
                <div
                  key={idx}
                  onClick={() => day && onSelectDate(day)}
                  className={`text-center p-1 text-sm cursor-pointer
                                            ${
                                              day &&
                                              selectedDate &&
                                              day.toDateString() ===
                                                selectedDate.toDateString()
                                                ? "bg-[#66708F] text-white rounded-lg"
                                                : ""
                                            }
                                            ${
                                              day &&
                                              !selectedDate &&
                                              day.toDateString() ===
                                                new Date().toDateString()
                                                ? "bg-white text-[#66708F]"
                                                : ""
                                            }`}
                >
                  {day ? day.getDate() : ""}
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2 items-center">
              <select
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="border rounded p-1 text-sm"
              >
                {hoursOptions.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
              :
              <select
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                className="border rounded p-1 text-sm"
              >
                {minutesOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                value={amPm}
                onChange={(e) => setAmPm(e.target.value)}
                className="border rounded p-1 text-sm"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>

            <div className="mt-4 text-xs text-gray-500">
              All times are in (UTC+10:00) Canberra, Melbourne, Sydney
            </div>

            <div className="mt-4 text-right">
              <button
                type="button"
                className="p-2 bg-[#66708F] text-white rounded-md"
                onClick={() => {
                  const formatted = getDisplayValue();
                  onChange(formatted);
                  closePicker();
                }}
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
